import React, { useState, useEffect, useRef } from 'react';
import CustomImage from './CustomImage';
import defaultSettings from './settings.json';

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const WhenClosed = ({ open, settings: propSettings, bubble, setBubbleClosed }) => {
  const [showBubble, setShowBubble] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [settings, setSettings] = useState(propSettings || defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  
  // Bubble count ref
  const bubbleCount = useRef(0);
  
  // Derived values based on settings
  const bubbleColor = settings.color;
  const closeicon = 'https://flaticons.net/icon.php?slug_category=mobile-application&slug_icon=close';
  const cleanedName = settings.name?.replace(/\.[a-z]+$/, '') || '';
  const UsefullName = capitalizeFirstLetter(cleanedName);

  useEffect(() => {
    const loadExternalSettings = async () => {
      try {
        // Try to fetch settings from external file
        const response = await fetch('./settings.json');
        if (response.ok) {
          const externalSettings = await response.json();
          setSettings(externalSettings);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadExternalSettings();
    
    // Set up periodic check for updated settings (every 30 seconds)
    const intervalId = setInterval(loadExternalSettings, 30000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

    useEffect(() => {
    if (showBubble && setBubbleClosed) {
      setBubbleClosed(false);
    }
  }, [showBubble, setBubbleClosed]);
  
  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      // More reliable mobile detection
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobileCheck);
    };

    // Initial check
    checkIfMobile();

    // Add resize listener to handle orientation changes or browser resizing
    window.addEventListener('resize', checkIfMobile);

    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (bubbleCount.current >= 2 || !bubble || isMobile) return; // Don't show bubble on mobile or after 2 times or if bubble is disabled

    // Add a delay before showing the bubble
    const bubbleTimer = setTimeout(() => {
      setShowBubble(bubble && !isMobile);
      if (bubble && !isMobile) {
        bubbleCount.current += 1;
        // Add a delay before showing the close button
        const closeButtonTimer = setTimeout(() => {
          setShowCloseButton(true);
        }, 250); // 250ms delay for the close button
        return () => clearTimeout(closeButtonTimer); // Cleanup the close button timer
      } else {
        setShowCloseButton(false);
      }
    }, 1000); // 1000ms delay for the bubble

    return () => clearTimeout(bubbleTimer); // Cleanup the bubble timer on unmount or when bubble changes
  }, [bubble, isMobile]); // Add isMobile as a dependency

  const handleCloseBubble = () => {
    setShowBubble(false);
    setShowCloseButton(false);
    if (setBubbleClosed) {
      setBubbleClosed(true);
    }
  };
  // If on mobile, render a smaller button without bubble
  if (isMobile) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          textAlign: 'center',
        }}
      >
        {/* Button with the image - smaller for mobile */}
        <button
          onClick={open}
          style={{
            position: 'fixed',
            bottom: '10px',
            right: '10px',
            outline: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            transform: 'scale(0.7)', // Make the button 70% of its original size
            transformOrigin: 'bottom right', // Scale from bottom-right corner
          }}
        >
          <CustomImage color={settings.color} />
        </button>
      </div>
    );
  }

  // Full version for desktop only
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        textAlign: 'center',
      }}
    >
      {/* Conditionally render the bubble with animation */}
      <div
        style={{
          position: 'absolute',
          bottom: '90px',
          right: '10px',
          backgroundColor: bubbleColor,
          color: 'white',
          padding: '10px 15px',
          borderRadius: '20px',
          fontSize: '16px',
          width: '270px',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          textAlign: 'left',
          opacity: showBubble ? 1 : 0,
          transform: showBubble ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
          fontFamily: 'Arial, sans-serif',
          boxShadow: `0px 6px 12px rgba(0, 0, 0, 0.3)`,
          zIndex: 1,
        }}
      >
        {settings.whenClosedMessage || 'Hi, \nI am an AI assistant I can\nHelp answer your questions!'}
      </div>

      {/* Close button for the chat bubble */}
      <button
        onClick={handleCloseBubble}
        style={{
          position: 'absolute',
          bottom: '180px',
          right: '11px',
          backgroundColor: bubbleColor,
          border: 'none',
          outline: 'none',
          cursor: 'pointer',
          padding: 0,
          borderRadius: '50%',
          zIndex: 2,
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: showCloseButton ? 1 : 0,
          transform: showCloseButton ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
          boxShadow: `0px 4px 8px rgba(0, 0, 0, 0.3)`,
        }}
      >
        <img src={closeicon} alt="Close" style={{ width: '12px', height: '12px' }} />
      </button>

      {/* Button with the image - desktop only */}
      <button
        onClick={open}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          outline: 'none',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <CustomImage color={settings.color} />
      </button>
    </div>
  );
};

export default WhenClosed;